// Fila de envio no aparelho (decisão 7.2). Substitui outbox/delbox do protótipo: a vistoria
// (dados + fotos em Blob) fica no IndexedDB até a API confirmar. O envio é idempotente
// (idCliente), então reenviar nunca duplica.

import { api, carregarEu, ErroApi } from './api.js';

const BANCO = 'lbx-vistoria';
const LOJA = 'envios';

export interface ArquivoFila { campo: string; blob: Blob; nome: string }
export interface ItemFila {
  idCliente: string;
  rota: '/api/vistorias' | '/api/vistorias/anexo';
  dados: any;
  arquivos: ArquivoFila[];
  criadoEm: string;
  /** Rótulo para a tela de ajustes. */
  rotulo: string;
  /** Mensagem da API quando ela recusou o envio (não é reenviado sozinho). */
  recusado?: string;
}

/** Itens que ainda vão ser reenviados (os recusados não contam). */
export async function contarPendentes(): Promise<number> { return (await listarFila()).filter((i) => !i.recusado).length; }

function abrir(): Promise<IDBDatabase> {
  return new Promise(function (res, rej) {
    const r = indexedDB.open(BANCO, 1);
    r.onupgradeneeded = function () { r.result.createObjectStore(LOJA, { keyPath: 'idCliente' }); };
    r.onsuccess = function () { res(r.result); };
    r.onerror = function () { rej(r.error); };
  });
}

async function tx<T>(modo: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  const db = await abrir();
  return new Promise<T>(function (res, rej) {
    const t = db.transaction(LOJA, modo);
    const req = fn(t.objectStore(LOJA));
    req.onsuccess = function () { res(req.result as T); };
    req.onerror = function () { rej(req.error); };
  });
}

export function adicionarNaFila(item: ItemFila) { return tx<any>('readwrite', (s) => s.put(item)); }
export function removerDaFila(id: string) { return tx<any>('readwrite', (s) => s.delete(id)); }
export function listarFila(): Promise<ItemFila[]> { return tx<ItemFila[]>('readonly', (s) => s.getAll()).catch(() => []); }
export async function contarFila(): Promise<number> { return (await listarFila()).length; }

export const sync: { last: Date | null; err: string | null } = { last: null, err: null };

function montarForm(item: ItemFila): FormData {
  const f = new FormData();
  f.append('dados', JSON.stringify(item.dados));
  item.arquivos.forEach(function (a) { f.append(a.campo, a.blob, a.nome); });
  return f;
}

/** Envia um item; devolve a VistoriaDTO criada. Em sucesso, sai da fila. */
export async function enviarItem(item: ItemFila): Promise<any> {
  const d = await api('POST', item.rota, montarForm(item), { silencioso: true });
  await removerDaFila(item.idCliente);
  return d;
}

let _enviando = false;
/** Protótipo: retryOutbox. Para no primeiro erro (sem rede, sessão vencida). */
export async function enviarPendentes(): Promise<void> {
  if (_enviando) return;
  _enviando = true;
  try {
    const itens = (await listarFila()).filter((i) => !i.recusado);
    if (!itens.length) return;
    // o token CSRF vem da sessão atual: sem ela (sem rede ou sessão vencida) não adianta tentar
    try { await carregarEu({ silencioso: true }); } catch (e: any) {
      sync.err = e instanceof ErroApi && e.status === 401 ? 'sessão encerrada: entre de novo para enviar' : 'sem conexão';
      return;
    }
    for (const it of itens) {
      try {
        await enviarItem(it);
        sync.err = null;
      } catch (e: any) {
        sync.err = e instanceof ErroApi && e.status === 401 ? 'sessão encerrada: entre de novo para enviar' : (e.message || 'sem conexão');
        // Recusa de validação (4xx que não seja 401) não passa nunca: marca e segue com as outras.
        // Nada é apagado do aparelho; a tela Ajustes gerais mostra a recusa.
        if (e instanceof ErroApi && (e.status === 400 || e.status === 413 || e.status === 422)) {
          it.recusado = e.message;
          await adicionarNaFila(it);
          continue;
        }
        break;
      }
    }
    sync.last = new Date();
  } finally {
    _enviando = false;
  }
}
